/**
 * Old → new tab paths, recorded when a saved new record renames its tab
 * (`/app/X/new` → `/app/X/<id>`).
 *
 * The host builds each body's callbacks from the path of the current render, so
 * "Save and close" hands `onSaved` and `onClose` the SAME captured path — but
 * `onSaved` renames the tab first. Without this map the close then targeted a
 * path that no longer existed and silently did nothing: the form stayed open
 * and only a second press closed it.
 *
 * Leaf module on purpose: both the store (which records) and the close helper
 * (which resolves) use it, and neither may import the other.
 */
const aliases = new Map<string, string>()

/** Bounds the walk — a cycle could only come from a bug, never from data. */
const MAX_HOPS = 8

export function noteTabPathReplaced(oldPath: string, newPath: string): void {
  if (!oldPath || !newPath || oldPath === newPath) return
  aliases.set(oldPath, newPath)
}

/** The path a tab lives at today, following any renames. */
export function resolveLiveTabPath(path: string): string {
  let current = path
  for (let hop = 0; hop < MAX_HOPS; hop += 1) {
    const next = aliases.get(current)
    if (!next || next === current) return current
    current = next
  }
  return current
}

/**
 * Drop rename edges that mention this path. Required when:
 * - the renamed tab is closed (`/id` gone → `/new→/id` must die), or
 * - a fresh tab reclaims `/new` after a prior save (otherwise Close on the
 *   new form resolves to the dead `/id` and silently no-ops).
 */
export function forgetTabPath(path: string): void {
  if (!path) return
  aliases.delete(path)
  Array.from(aliases.entries()).forEach(([from, to]) => {
    if (to === path) aliases.delete(from)
  })
}

export function clearTabPathAliases(): void {
  aliases.clear()
}
